import type { ReportParameters } from "../reducers/common";

/** Minimal Redux fields needed to decide whether to prefetch the export baseline parallel report UUID. */
export type ExportBaselineParallelPrefetchSnapshot = {
    /** Main Gantt report finished successfully (data present, not loading). */
    mainReportReady: boolean;
    /** Baseline parallel report UUID; null until prefetched or after reset. */
    parallel_original_report_uuid: string | null;
    /** Last report parameters synced for parallel export (from `gantt_chart_parallel_report`). */
    parameters: ReportParameters | null;
};

/**
 * True when both configs use the same planning scope (plant + line group).
 * Caller must pass non-null `ReportParameters`.
 */
export function arePlanningScopesEqual(params1: ReportParameters, params2: ReportParameters): boolean {
    return (
        params1.config.line_group_uuid === params2.config.line_group_uuid &&
        params1.config.plant_uuid === params2.config.plant_uuid
    );
}

/**
 * True when none of the prefetch-relevant fields changed between two store snapshots.
 *
 * `parameters` uses reference equality on purpose: planning reducers should replace the object when
 * parameters change: in-place mutation would not fire subscribers and is not supported here.
 */
export function areBaselineParallelPrefetchSnapshotsEqual(
    snapshot1: ExportBaselineParallelPrefetchSnapshot,
    snapshot2: ExportBaselineParallelPrefetchSnapshot
): boolean {
    return (
        snapshot1.mainReportReady === snapshot2.mainReportReady &&
        snapshot1.parallel_original_report_uuid === snapshot2.parallel_original_report_uuid &&
        snapshot1.parameters === snapshot2.parameters
    );
}

/**
 * Synthetic “previous” snapshot for the first store notification (no prior snapshot).
 * Forces the catch-up branch so we prefetch when the app is already main-ready on first subscribe.
 */
export function syntheticFirstPrefetchPrev(
    next: ExportBaselineParallelPrefetchSnapshot
): ExportBaselineParallelPrefetchSnapshot {
    return { ...next, mainReportReady: false };
}

/**
 * After an async parallel sim, true if we may still commit the UUID: main still ready, params present,
 * same planning scope as the request that was in flight.
 */
export function isBaselineParallelPrefetchContextUnchanged(
    context: ExportBaselineParallelPrefetchSnapshot,
    requestParameters: ReportParameters
): boolean {
    const { mainReportReady, parameters } = context;
    return mainReportReady && parameters !== null && arePlanningScopesEqual(parameters, requestParameters);
}

/**
 * True when parallel / parameter state suggests we may need a new baseline UUID (ignores main Gantt readiness).
 */
export function shouldReconsiderBaselineParallelPrefetch(
    prev: ExportBaselineParallelPrefetchSnapshot,
    next: ExportBaselineParallelPrefetchSnapshot
): boolean {
    const prevReportParameters = prev.parameters;
    const reportParameters = next.parameters;

    const scopeChanged =
        prevReportParameters !== null &&
        reportParameters !== null &&
        !arePlanningScopesEqual(prevReportParameters, reportParameters);

    const reportParametersBecameAvailable =
        prevReportParameters === null && reportParameters !== null && next.parallel_original_report_uuid === null;

    const parallelReportUuidCleared =
        prev.parallel_original_report_uuid !== null &&
        next.parallel_original_report_uuid === null &&
        reportParameters !== null;

    return scopeChanged || reportParametersBecameAvailable || parallelReportUuidCleared;
}

/**
 * Whether to start prefetch now: main Gantt ready, baseline UUID still missing, parameters present,
 * and either `shouldReconsiderBaselineParallelPrefetch` or main was not ready on the previous snapshot.
 */
export function shouldPrefetchBaselineParallelReportUuid(
    prev: ExportBaselineParallelPrefetchSnapshot,
    next: ExportBaselineParallelPrefetchSnapshot
): boolean {
    const { mainReportReady, parallel_original_report_uuid: reportUuid, parameters } = next;

    return (
        mainReportReady &&
        reportUuid === null &&
        parameters !== null &&
        (shouldReconsiderBaselineParallelPrefetch(prev, next) || !prev.mainReportReady)
    );
}
